import React, { useCallback, useEffect, useState } from "react";
import Select from "react-select";
import Spacing from "../../Spacing";
import { useDocumentTitle } from "../../../hooks/customDocumentTitleHook";
import { debounce, getPractice } from "../../../helpers";
import { Search } from "lucide-react";
import DataTablePagination from "../../DataTablePagination";
import { fetchAllSpecialist, getAllUSStates } from "../../../services/easyReferral";
import NpiEditModal from "../../Modal/NpiEditModal";
import NPIDatabaseDataTable from "../../DataTable/NPIDatabaseDataTable";

export default function NPIDatabase({selectedPracticeChange,fetchChangePracticeData}) {
  let location_id = getPractice("location_id");
  let account_id = getPractice("account_id");
  if(selectedPracticeChange != null && fetchChangePracticeData){
    location_id = selectedPracticeChange?.location_id;
    account_id = selectedPracticeChange?.account_id;
  }

  const [specialistData, setSpecialistData] = useState([]);
  const [specialistDataLoading, setSpecialistDataLoading] = useState(true);
  const [statesData, setStatesData] = useState([]);
  const [editNpiData, setEditNpiData] = useState({});
  const [editNpiModal, setEditNpiModal] = useState(false);
  const [previousScrollPosition, setPreviousScrollPosition] = useState(0);

  const defaultSpecialistFilter = {
    account_id: account_id,
    location_id: location_id,
    page: 1,
    per_page: 10,
    state: "NY",
    search_term: "",
    provider_name: "",
    practice_name: "",
    city: "",
    practice_state: "",
    postal_code: "",
    speciality_ids: []
  };
  const [specialistFilter, setSpecialistFilter] = useState(defaultSpecialistFilter);
  const [specialistDataCount, setSpecialistDataCount] = useState({
    totalCount: 0,
    totalPages: 0,
  });

  useEffect(() => {
    getAllStates();
  }, []);

  const getAllStates = async () => {
    const response = await getAllUSStates();
    if (response) {
      setStatesData(response);
      const allStatusOptions = response?.map((option) => ({
        value: option?.sort_name,
        label: option?.name,
      }));
      setStatesData(allStatusOptions);
    }
  }

  useEffect(() => {
    const sameLocation = specialistFilter.location_id === location_id;
    const sameAccount = specialistFilter.account_id === account_id;

    if (sameLocation && sameAccount) return;

    setSpecialistFilter({
      ...specialistFilter,
      location_id: location_id,
      account_id: account_id,
    });
    setSpecialistDataLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location_id, account_id]);

  useEffect(() => {
    loadSpecialistData(specialistFilter);
  }, [specialistFilter]);

  const loadSpecialistData = async (getFilter) => {
    setSpecialistFilter(getFilter);
    try {
      const response = await fetchAllSpecialist(getFilter);
      if (response) {
        if(!response?.detail) {
          const generateData = response?.data?.results;
          setSpecialistData(generateData);
          setSpecialistDataCount({
            totalCount: response?.data?.count,
            totalPages: response?.data?.total_pages,
          });
        }
      }
    } catch (error) {} finally {
      setSpecialistDataLoading(false);
    }
  };

  const debouncedSetSpecialistFilter = useCallback(
    debounce((newFilter) => setSpecialistFilter(newFilter), 300), []
  );

  const handleChangeSpecialistSearchTerm = (event) => {
    const newFilter = { ...specialistFilter, search_term: event.target.value, page: 1 };
    setSpecialistDataLoading(true);
    debouncedSetSpecialistFilter(newFilter);
  };

  const handleSpecialistPageChange = (newPage) => {
    setSpecialistFilter({ ...specialistFilter, page: newPage });
    setSpecialistDataLoading(true);
  };

  const handleChangeSpecialistRowsPerPage = (value) => {
    setSpecialistFilter({
      ...specialistFilter,
      page: 1,
      per_page: value,
    });
    setSpecialistDataLoading(true);
  };

  const handelChangeState = (selectedOption) => {
    setSpecialistDataLoading(true);
    setSpecialistFilter({
      ...specialistFilter,
      state: selectedOption?.value || "NY",
      page: 1,
    });
  }

  useDocumentTitle("NPI Database");

  return (
    <>
      <div
        className="flex items-center justify-between"
        style={{ justifyContent: "space-between", gap: "15px 20px" }}
      >
        <h2 className="docpro_fs_24 docpro_mb_0">NPI Database</h2>
      </div>
      <Spacing lg="10" sm="15" />
      <div className="docpro_data_table_wrap">
        <div className="docpro_data_table_headings">
          <div className="flex items-center">
            <form className="docpro_search_form width_100">
              <input
                type="text"
                placeholder="Search"
                className="docpro_search_input"
                onChange={handleChangeSpecialistSearchTerm}
              />
              <button className="docpro_search_btn">
                <Search width={20} height={20} />
              </button>
            </form>
          </div>

          <div className="docpro_data_table_btns p-0">
            <Select
              options={statesData}
              placeholder="Search..."
              isSearchable
              value={statesData.find((option) => option?.value === specialistFilter?.state) || null}
              onChange={handelChangeState}
              className="react-select-container"
              classNamePrefix="react-select"
              filterOption={(option, rawInput) => {
                const words = rawInput.toLowerCase().split(" ");
                return words.every((word) =>
                  option.label.toLowerCase().includes(word)
                );
              }}
              styles={{
                control: (base) => ({
                  ...base,
                  minHeight: '36px',
                  borderColor: 'var(--border)',
                  borderRadius: '8px',
                  minWidth: '150px'
                }),
                dropdownIndicator: (base) => ({
                  ...base,
                  padding: '6px',
                }),
                menu: (base) => ({
                  ...base,
                  zIndex: 1050,
                  marginTop: '-2px',
                  borderRadius: '0 0 3px 3px',
                }),
                menuPortal: (base) => ({
                  ...base,
                  zIndex: 1050,
                  padding: 0
                }),
              }}
            />

            {/* <Filter
              filterType="specialist"
              reloadData={loadSpecialistData}
              reloadDataFilter={specialistFilter}
              setDataLoading={setSpecialistDataLoading}
              patientFormData={specialistFormData}  // Pass down patientFormData
              setPatientFormData={setSpecialistFormData}  // Pass down setPatientFormData
            /> */}
          </div>
        </div>

        <NPIDatabaseDataTable
          tableData={specialistData}
          dataLoading={specialistDataLoading}
          setEditNpiData={setEditNpiData}
          setEditNpiModal={setEditNpiModal}
          setPreviousScrollPosition={setPreviousScrollPosition}
        />
        {specialistData.length > 0 && !specialistDataLoading && (
          <DataTablePagination
            currentPage={specialistFilter.page}
            totalPages={specialistDataCount.totalPages}
            onPageChange={handleSpecialistPageChange}
            handleChangeRowsPerPage={handleChangeSpecialistRowsPerPage}
            totalRecords={specialistDataCount.totalCount}
            currentPageData={specialistFilter.per_page}
          />
        )}
      </div>

      {editNpiModal && (
        <NpiEditModal 
          reloadData={loadSpecialistData}
          filter={specialistFilter}
          editNpiData={editNpiData}
          setEditNpiData={setEditNpiData}
          editNpiModal={editNpiModal}
          setEditNpiModal={setEditNpiModal}
          account_id={account_id}
          setSpecialistDataLoading={setSpecialistDataLoading}
          previousScrollPosition={previousScrollPosition}
        />
      )}
    </>
  );
}
